// assets/age-fit.js
// Age-appropriate routing at DOB entry. See docs/digitals/age-appropriate-routing.md.
// From the birth DATE it routes to the age-right edition (kids: Your Sky Story; 13-17: the younger
// edition of The Book of You; 18+: full) and keeps the adult book off a child. A warm
// redirect, never a wall. Sets the _Edition line-item property the worker + generator read.
// Sibling to birth-data-gate.js; only present on products flagged custom.audience == 'adult'.
//
// Thresholds mirror reading/preflight.mjs AGE_BANDS (the machine-readable SSOT). Change both + the doc.
import { BIRTH_SELECTORS, onBirthDataChange } from './birth-data.js';

const CHILD_MAX = 12; // 0..12 -> Your Sky Story
const AFFIRM_FROM = 16; // 16..17 may unlock the full edition with an adult affirmation
const FULL_FLOOR = 18; // 18+ -> the full book, no gate

/** @param {string} dateStr @param {Date} now */
function classify(dateStr, now) {
  if (!dateStr) return { band: null };
  const b = new Date(dateStr + 'T00:00:00');
  if (Number.isNaN(+b)) return { band: null };
  if (b > now) return { band: 'future' };
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age -= 1;
  if (age <= CHILD_MAX) return { band: 'child', age };
  if (age < FULL_FLOOR) return { band: 'young', age, canFull: age >= AFFIRM_FROM };
  return { band: 'adult', age };
}

class AgeFit extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.dateEl = /** @type {HTMLInputElement|null} */ (document.querySelector(BIRTH_SELECTORS.date));
    if (!this.dateEl) return; // no birth-date field -> nothing to route
    this.card = /** @type {HTMLElement|null} */ (this.querySelector('[data-age-card]'));
    this.editionInput = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-age-edition]'));
    this.affirm = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-age-affirm]'));
    this.skyUrl = this.dataset.skyUrl || '/pages/your-sky-story';
    const inComp = this.closest('product-form-component');
    this.form =
      (this.dataset.formId ? document.getElementById(this.dataset.formId) : null) ||
      (inComp ? inComp.querySelector('form') : null) ||
      document.querySelector('form[data-type="add-to-cart-form"]');
    this.form?.addEventListener('submit', (e) => this.onSubmit(e), true);
    onBirthDataChange(() => this.update());
    this.update();
    this.hidden = false;
  }

  update() {
    this.state = classify((this.dateEl && this.dateEl.value.trim()) || '', new Date());
    const c = this.state;
    if (this.card) {
      if (!c.band || c.band === 'adult') { this.card.hidden = true; this.card.innerHTML = ''; }
      else { this.card.innerHTML = this.render(c); this.card.hidden = false; }
      const box = this.card.querySelector('[data-affirm-box]');
      if (box) box.addEventListener('change', () => this.applyEdition());
    }
    this.applyEdition();
  }

  /** Set the _Edition property + the 16-17 affirmation from the current band. */
  applyEdition() {
    const c = this.state || { band: null };
    const box = /** @type {HTMLInputElement|null} */ (this.card && this.card.querySelector('[data-affirm-box]'));
    const affirmed = Boolean(box && box.checked);
    let edition = 'full';
    if (c.band === 'young') edition = c.canFull && affirmed ? 'full' : 'younger';
    if (this.editionInput) this.editionInput.value = edition;
    if (this.affirm) {
      const on = c.band === 'young' && c.canFull && affirmed;
      this.affirm.disabled = !on;
      this.affirm.value = on ? new Date().toISOString() : '';
    }
  }

  /** @param {{band:string, age?:number, canFull?:boolean}} c */
  render(c) {
    if (c.band === 'future')
      return `<p class="age-fit__msg age-fit__msg--warn">That date is in the future. Mind checking it again?</p>`;
    if (c.band === 'child')
      return `<p class="age-fit__msg"><strong>Your Sky Story</strong> is the keepsake we make for a reader this young. It is illustrated, gentle, and made to be read together. The Book of You is written for grown readers.</p>
        <a class="age-fit__cta" href="${this.skyUrl}">See Your Sky Story &rarr;</a>`;
    if (c.band === 'young' && !c.canFull)
      return `<p class="age-fit__msg">This reader will receive the edition of The Book of You written for younger readers. It is the same book, with its most grown up chapters gently adapted.</p>`;
    // young 16-17: younger by default, full available with an adult affirmation
    return `<p class="age-fit__msg">We would write this as the edition for younger readers. If you are an adult choosing the full edition knowingly for this reader, you can continue:</p>
      <label class="age-fit__affirm"><input type="checkbox" data-affirm-box> I am an adult and have chosen the full edition knowingly. It includes adult relational and reflective themes.</label>`;
  }

  /** @param {SubmitEvent} e */
  onSubmit(e) {
    const c = this.state;
    if (!c) return;
    // Block only the two cases the buyer must resolve: a future date (typo) and a young child (the
    // adult book is not for them). Teens proceed with _Edition=younger; 18+ proceed as full. The
    // worker's server floor is the backstop.
    if (c.band === 'future' || c.band === 'child') {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (this.card) { this.card.hidden = false; this.card.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
    }
  }
}

if (!customElements.get('age-fit')) customElements.define('age-fit', AgeFit);
